import React from 'react';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';

import { getDashboardPanelTokens, rem } from '../dashboardPanelTokens';
import type { CoverageView } from './coverage.types';
import { CoverageTile } from './CoverageTile';
import { CoverageGroupRow } from './CoverageGroupRow';

interface CoveragePanelProps {
  readonly view: CoverageView;
  readonly companyName: string;
  readonly isLoading?: boolean;
  readonly isError?: boolean;
  readonly now?: string | number | Date;
}

/**
 * The dashboard's coverage tile.
 *
 * Renders the view it is handed and nothing more -- no fetching, no searching
 * for "the" policy or "the" quote. Which group leads and which decisions are
 * pending were settled in `buildCoverageView`, so this file has one job:
 * hero, then the rest as rows.
 */
export function CoveragePanel({
  view,
  companyName,
  isLoading = false,
  isError = false,
  now = Date.now(),
}: Readonly<CoveragePanelProps>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);

  if (isLoading) {
    return (
      <Box data-qa="COVERAGE_PANEL_LOADING" sx={{ width: '100%' }}>
        <Skeleton variant="text" width="30%" height={rem(20)} />
        <Skeleton variant="text" width="55%" height={rem(38)} />
        <Skeleton
          variant="rectangular"
          height={rem(72)}
          sx={{ marginTop: rem(16) }}
        />
      </Box>
    );
  }

  // A failed read is not the same as an account with no coverage, and must not
  // be shown as one -- telling an insured they have no cover because a request
  // timed out is the worst thing this tile could do.
  if (isError) {
    return (
      <Box data-qa="COVERAGE_PANEL_ERROR" sx={{ width: '100%' }}>
        <Typography
          sx={{ fontSize: rem(14), fontWeight: 700, color: tokens.ink }}
        >
          We couldn&rsquo;t load your coverage
        </Typography>
        <Typography
          sx={{ marginTop: rem(6), fontSize: rem(13), color: tokens.inkBody }}
        >
          Your policies and quotes are unaffected. Refresh the page, or check
          the Policies tab for the full list.
        </Typography>
      </Box>
    );
  }

  // Nothing at all, rather than a prospect panel. An account with no policy and
  // no quote has nothing to say here, and a pitch in the page's leading slot
  // pushed the things it does have -- factors, claims, activity -- down the page.
  if (!view.hasAnyCoverage || !view.hero) {
    return null;
  }

  const pendingCount = view.attention.filter(
    (item) => item.groupKey !== view.hero?.key
  ).length;

  return (
    <Box
      data-qa="COVERAGE_PANEL"
      // Passes the card's height through to the tile, which is what claims any
      // slack the row has. Inert wherever the card is not a flex column.
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        width: '100%',
        minWidth: 0,
      }}
    >
      <CoverageTile group={view.hero} insuredName={companyName} now={now} />

      {view.others.length > 0 && (
        <Box sx={{ marginTop: rem(28) }}>
          <Box
            display="flex"
            flexWrap="wrap"
            alignItems="baseline"
            justifyContent="space-between"
            gap={rem(12)}
            sx={{ paddingBottom: rem(8) }}
          >
            <Typography
              component="h4"
              sx={{
                margin: 0,
                fontSize: rem(14),
                fontWeight: 700,
                color: tokens.ink,
              }}
            >
              Also on this account
            </Typography>
            <Typography sx={{ fontSize: rem(12), color: tokens.inkFaint }}>
              {view.others.length} {view.others.length === 1 ? 'line' : 'lines'}
              {pendingCount > 0 && ` · ${pendingCount} awaiting your decision`}
            </Typography>
          </Box>

          <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {view.others.map((group) => (
              <CoverageGroupRow key={group.key} group={group} now={now} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
